import { Stack, Card, Group, Switch, ActionIcon } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { GetSensorResponse } from 'api/actions/get-sensor/get-sensor-response';
import { Link } from 'react-router-dom';
import theme from 'theme';
import P1Medium from 'theme/components/Typography/P1Medium';
import P2Medium from 'theme/components/Typography/P2Medium';
import P2Regular from 'theme/components/Typography/P2Regular';

export type SensorAlert = NonNullable<GetSensorResponse['sensor']['alerts']>[number];

interface SensorAlertsPresenterProps {
  sensorId?: number;
  alerts: SensorAlert[];
  _t: (msgid: string) => string;
  onToggle: (alertType: SensorAlert['type'], currentStatus: boolean | undefined) => void;
}

/**
 * Returns the English source title for a known alert type.
 * @param alert The alert metadata.
 */
const getAlertTitleKey = (alert: SensorAlert) => {
  if (alert.type === '24h-consumption') {
    return 'High consumption';
  }

  if (alert.type === 'water-usage') {
    return 'Possible water leak';
  }

  if (alert.type === 'lower-limit') {
    return 'Low value';
  }

  if (alert.type === 'upper-limit') {
    return 'High value';
  }

  return alert.title ?? '';
};

/**
 * Renders the sensor alerts UI independently of how alert state is persisted.
 */
export default function SensorAlertsPresenter({ sensorId, alerts, _t, onToggle }: SensorAlertsPresenterProps) {
  if (!alerts || alerts.length === 0) {
    return null;
  }

  const alertDetailPath = sensorId ? `/sensors/sensor/edit/${sensorId}` : undefined;

  /**
   * Returns the secondary text for an alert.
   * @param alert The alert to format.
   */
  const getSecondaryText = (alert: SensorAlert) => {
    if (!alert.active) {
      return null;
    }

    const limitField = alert.fields?.find((field) => field.name === 'limit' && field.type === 'number');

    if (alert.type === '24h-consumption' && limitField?.value !== undefined && limitField?.value !== null) {
      return `${limitField.value} ${limitField.unit ?? 'l'} / 24h`;
    }

    if (
      (alert.type === 'lower-limit' || alert.type === 'upper-limit') &&
      limitField?.value !== undefined &&
      limitField?.value !== null
    ) {
      return `${limitField.value} ${limitField.unit ?? ''}`.trim();
    }

    return null;
  };

  return (
    <Stack gap={16}>
      <P2Medium color="gray.7">{_t('Alerts')}</P2Medium>
      <Card py={8} px={16} radius={10}>
        <Stack gap={8} py={8}>
          {alerts.map((alert) => {
            const secondaryText = getSecondaryText(alert);
            const titleKey = getAlertTitleKey(alert);

            return (
              <Group key={alert.type ?? alert.title} gap={16} wrap="nowrap" align="center">
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <P1Medium color="gray.12">{titleKey ? _t(titleKey) : ''}</P1Medium>
                  {secondaryText ? <P2Regular color="gray.7">{secondaryText}</P2Regular> : null}
                </Stack>
                <Switch
                  checked={alert.active}
                  onChange={() => onToggle(alert.type, alert.active)}
                  color="#551FFF"
                  size="lg"
                  styles={{ track: { width: '56px', height: '32px' } }}
                />
                {alert.configurable !== false && alertDetailPath ? (
                  <ActionIcon
                    component={Link}
                    to={alertDetailPath}
                    variant="subtle"
                    color="gray"
                    size="lg"
                    aria-label={titleKey ? `${_t(titleKey)} detail` : _t('Open alert detail')}
                  >
                    <IconChevronRight size={24} color={theme.colors?.gray?.[7]} />
                  </ActionIcon>
                ) : (
                  <div style={{ width: 34 }} />
                )}
              </Group>
            );
          })}
        </Stack>
      </Card>
    </Stack>
  );
}
